import React, {
  useContext, useMemo, useRef, useState, useEffect, useCallback,
} from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { useDispatch, useSelector } from 'react-redux';

import { selectTotalBookmarkCount } from 'models/bookmarks';
import { setFullscreen } from 'models/twilio';
import { selectAvailableLocales } from 'models/localization';
import { UserSessionContext } from 'components/UserSession';
import { SearchFieldStateProvider } from 'components/SearchFieldContext';

import Routes from 'Routes';
import BookmarksModal from 'components/modals/bookmarks/BookmarksModal';
import LocaleSelector from './LocaleSelector';
import HelpButton from './HelpButton';
import UserAccountView from './UserAccountView';
import SearchField from './desktop/SearchField';
import BookmarksButton from './BookmarksButton';
import DesktopAppBarCustomPage from './desktop/AppBarCustomPages';

const RootContainer = styled.div`
  width: 100%;
  z-index: 1000;
  position: relative;
`;

const StyledAppBar = styled(AppBar)`
  color: #484848;
  background: #FFF;
`;

const StyledToolbar = styled(Toolbar)`
  display: flex;

  & * {
    align-items: center;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  margin-right: auto;
  flex: 2 0 auto;
`;

const Logo = styled.img<{ height: number }>`
  display: inline;
  align-self: center;
  height: ${({ height }) => height}px;
  vertical-align: middle;
  margin-right: 24px;
`;

const LogoLink = styled(Link)`
  display: inline-flex;
`;

const ActionContainer = styled.div`
  display: flex;
  margin-left: auto;
  flex: 2 0 auto;
  justify-content: flex-end;
`;

const StyledMenuItemContainer = styled.div`
  display: flex;
  overflow: hidden;
  flex: 1 1 auto;
  justify-content: space-between;

  & * {
    white-space: nowrap;
  }
`;

const MenuItemContainer = () => {
  const ref = useRef<React.ElementRef<'div'>>();
  const containerWidthRef = useRef(0);
  const previousAnimationTimeRef = useRef(0);
  const [showMoreFrom, setShowMoreFrom] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (!ref.current) return;

      Array.from(ref.current.children)
        .forEach((item: any) => {
          item.style.display = '';
        });

      const moreContainerRef: any = ref.current.lastElementChild?.id === 'more-container'
        ? ref.current.lastElementChild
        : null;
      if (!moreContainerRef) return;
      let totalItemWidth = 0;
      let validIndex = 0;
      const moreContainerWidth = moreContainerRef.offsetWidth || 0;
      const containerWidth = ref.current.offsetWidth - moreContainerWidth;
      const childCount = ref.current.childElementCount;

      Array.from(ref.current.children)
        .forEach((item: any) => {
          totalItemWidth += item.offsetWidth || 0;
          if (containerWidth < totalItemWidth) {
            item.style.display = 'none';
            return;
          }
          validIndex += 1;
        });

      if (validIndex === ref.current.childElementCount || childCount === 1) {
        moreContainerRef.style.display = 'none';
      } else if (validIndex === childCount - 2 && containerWidth + moreContainerWidth > totalItemWidth - moreContainerWidth) {
        (ref.current.children[validIndex] as any).style.display = '';
        validIndex += 1;
      } else if (validIndex !== ref.current.childElementCount - 1) {
        moreContainerRef.style.display = '';
      }

      setShowMoreFrom(validIndex);
    };

    let id: number;
    const loop = (time: number) => {
      if (
        time - previousAnimationTimeRef.current > 100
        && ref.current
        && containerWidthRef.current !== ref.current.offsetWidth
      ) {
        previousAnimationTimeRef.current = time;
        containerWidthRef.current = ref.current.offsetWidth;
        handleResize();
      }
      id = requestAnimationFrame(loop);
    };

    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <StyledMenuItemContainer ref={ref}>
      <DesktopAppBarCustomPage
        keepMounted
        showMoreFrom={showMoreFrom}
      />
    </StyledMenuItemContainer>
  );
};

export interface DesktopAppBarProps {
  logos: any[];
  hideUser?: boolean;
  hideSearchBar?: boolean;
  bookmarksModalOpen?: boolean;
  openBookmarks?: () => any;
  closeBookmarks?: () => any;
}

const DesktopAppBar: React.FC<DesktopAppBarProps> = ({
  logos,
  hideUser,
  hideSearchBar,
  bookmarksModalOpen,
  openBookmarks = () => null,
  closeBookmarks = () => null,
}: DesktopAppBarProps) => {
  const dispatch = useDispatch();
  const { canSee, firstVisibleRoute, userSession } = useContext(UserSessionContext);
  const { features } = useSelector((state: any) => state.eventConfigs);
  const availableLocales = useSelector(selectAvailableLocales);
  const showSelectedLocale = useMemo(() => features.includes('showSelectedLocaleOnTopBar'), [features]);
  const totalBookmarks = useSelector(selectTotalBookmarkCount);

  const toolbarOnClick = useCallback(() => {
    dispatch(setFullscreen(false));
  }, [dispatch]);

  return (
    <RootContainer>
      <StyledAppBar
        position="static"
        elevation={3}
      >
        <StyledToolbar onClick={toolbarOnClick}>
          <LogoContainer>
            {logos.map((logo) => {
              const key = `${logo.src}-${logo.redirectTo}`;
              const to = logo.redirectUrl || Routes[firstVisibleRoute];
              const child = <Logo height={logo.height} src={logo.src} alt="Event Logo" />;
              if (logo.redirectExternal) return <a key={key} href={to} target="_blank" rel="noopener noreferrer">{child}</a>;
              return <LogoLink key={key} to={to}>{child}</LogoLink>;
            })}
          </LogoContainer>
          <MenuItemContainer />
          <ActionContainer>
            {canSee('lobby') && features.includes('bookmarks') && (
              <>
                {(totalBookmarks > 0) && <BookmarksButton openBookmarks={openBookmarks} />}
                <BookmarksModal open={bookmarksModalOpen} onClose={closeBookmarks} />
              </>
            )}
            {
              !hideSearchBar && canSee('search') && (
                <SearchFieldStateProvider>
                  <SearchField />
                </SearchFieldStateProvider>
              )
            }
            {availableLocales.length > 1 && <LocaleSelector showSelectedLocale={showSelectedLocale} />}
            <HelpButton />
            {!hideUser && userSession && <UserAccountView />}
          </ActionContainer>
        </StyledToolbar>
      </StyledAppBar>
    </RootContainer>
  );
};

export default DesktopAppBar;
